import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { authStore, AuthStore } from '@/store/authStore'
import { authService } from '@/services/authService'
import { LoginRequest, TotpEnrollResponse } from '@/types/auth'

/**
 * Resultado do login, discriminado para guiar o fluxo de 2FA na LoginPage:
 *  - `ok`     → sessão criada, já navegou para o dashboard.
 *  - `totp`   → senha aceita; falta o código do autenticador (etapa de verificação).
 *  - `enroll` → usuário ainda não cadastrou o 2FA (mostrar tela de QR).
 *  - `error`  → falha real (credenciais inválidas, etc.); mensagem em `message`.
 */
export type LoginOutcome =
  | { status: 'ok' }
  | { status: 'totp' }
  | { status: 'enroll' }
  | { status: 'error'; message: string }

const errorCodeOf = (error: unknown): string | undefined =>
  (error as { data?: { errorCode?: string } })?.data?.errorCode
const messageOf = (error: unknown): string =>
  (error as { message?: string })?.message ?? 'Erro ao processar requisição'

export const useAuth = () => {
  const navigate = useNavigate()
  const auth = authStore() as AuthStore

  const handleLogin = useCallback(
    async (credentials: LoginRequest): Promise<LoginOutcome> => {
      try {
        auth.setLoading(true)
        auth.setError(null)

        const response = await authService.login(credentials)
        // Tokens já vieram via Set-Cookie HttpOnly. Aqui só hidratamos
        // a UI com os dados de identidade do usuário.
        auth.loginSuccess(response)
        navigate('/dashboard')
        return { status: 'ok' }
      } catch (error) {
        // O interceptor do axios rejeita com { message, status, data } onde
        // `data` é o corpo do backend ({ success, message, errorCode }).
        const errorCode = errorCodeOf(error)
        if (errorCode === 'TOTP_ENROLL_REQUIRED') return { status: 'enroll' }
        if (errorCode === 'TOTP_REQUIRED') return { status: 'totp' }

        const message = messageOf(error)
        auth.setError(message)
        return { status: 'error', message }
      } finally {
        auth.setLoading(false)
      }
    },
    [auth, navigate],
  )

  /** Inicia o cadastro de 2FA (gera QR). Retorna null e seta erro em caso de falha. */
  const handleEnrollStart = useCallback(
    async (credentials: LoginRequest): Promise<TotpEnrollResponse | null> => {
      try {
        auth.setLoading(true)
        auth.setError(null)
        return await authService.totpEnroll(credentials)
      } catch (error) {
        auth.setError(messageOf(error))
        return null
      } finally {
        auth.setLoading(false)
      }
    },
    [auth],
  )

  /**
   * Conclui o cadastro de 2FA com o primeiro código. Em sucesso, hidrata a sessão
   * (cookies já emitidos) e retorna os códigos de recuperação para exibição — a
   * navegação ao dashboard fica a cargo da tela, após o usuário guardar os códigos.
   */
  const handleEnrollConfirm = useCallback(
    async (
      credentials: LoginRequest,
      setupToken: string,
      code: string,
    ): Promise<{ status: 'ok'; recoveryCodes: string[] } | { status: 'error'; message: string }> => {
      try {
        auth.setLoading(true)
        auth.setError(null)
        const { session, recoveryCodes } = await authService.totpConfirm(credentials, setupToken, code)
        auth.loginSuccess(session)
        return { status: 'ok', recoveryCodes }
      } catch (error) {
        const message = messageOf(error)
        auth.setError(message)
        return { status: 'error', message }
      } finally {
        auth.setLoading(false)
      }
    },
    [auth],
  )

  const handleLogout = useCallback(async () => {
    try {
      await authService.logout()
    } catch {
      // Continue logout even if API fails — backend já tem fallback
    } finally {
      auth.logout()
      navigate('/login')
    }
  }, [auth, navigate])

  /**
   * Renovação manual da sessão (raramente necessária — o axios
   * interceptor faz isso automaticamente em respostas 401).
   * O refresh_token vai via cookie HttpOnly.
   */
  const handleRefreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await authService.refresh()
      auth.loginSuccess(response)
      return true
    } catch {
      auth.logout()
      navigate('/login')
      return false
    }
  }, [auth, navigate])

  return {
    usuario: auth.usuario,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: auth.error,
    login: handleLogin,
    enrollStart: handleEnrollStart,
    enrollConfirm: handleEnrollConfirm,
    logout: handleLogout,
    refreshToken: handleRefreshToken,
  }
}
